import { z } from "zod";
import dayjs from "dayjs";
import type {
  DonationCampaign,
  MerchantTerm,
  TicketPersonFormField,
  Ticket,
} from "../../types/reservation";
import { DataTypeCode } from "../../common/enums/DataTypeCode";
import { getDonationAmountBounds } from "./utils/donationUtils";
import { formatPrice } from "../../utils/Utils";
import { useReservationResources } from "./hooks/useReservationResources";
import { KOK_AGE_CONDITIONAL_MAPS, getKokAgeFieldId, isKokAgeEligible, isValidMemberCard, isValidFinancialSupportCoupon, isValidSportsCard, isValidAmka, isAmkaHolderUnderAge, isOlympiacosFancardWaived } from "./utils/ticketPersonUtils";
import { PRODUCER_ID_OLYMPIACOS } from "../../common/producerIds";

type Resources = ReturnType<typeof useReservationResources>;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MIN_DATE_YEAR = 1900;
export const MAX_DATE_YEAR = 2099;
// Companion ticketPersonData key (see ticket-info.tsx renderDateField) flagging
// an optional date field as half-typed (e.g. only month+year) — MUI's DateField
// never publishes a value for that state, so there's nothing else to validate.
export const PARTIAL_DATE_SUFFIX = "__partial";
const AMKA_CERTIFICATE_VALUE = "5";
const AMKA_TICKET_PERSON_MAP = "Amka";
const TYPE_OF_CERT_MAP = "TypeOfCertificate";
const NATIONALITY_MAP = "Nationality";

// EVP-11021. Kids-ticket eligibility uses a 16-year age cap: a holder is
// considered a minor until the day before their 16th birthday.
const KIDS_TICKET_AGE_CAP = 16;
function isAmkaHolderMinor(amka: string, today: Date): boolean {
  return isAmkaHolderUnderAge(amka, today, KIDS_TICKET_AGE_CAP);
}

// Dynamic date attributes are stored as "DD-MM-YYYY" (see ticket-info.tsx
// renderDateField); bounds mirror the DateField's own minDate/maxDate.
function validateDateField(value: string, r: Resources): string | null {
  const parsed = dayjs(value, ["DD-MM-YYYY", "YYYY-MM-DD HH:mm:ss"], true);
  return parsed.isValid() &&
    parsed.year() >= MIN_DATE_YEAR &&
    parsed.year() <= MAX_DATE_YEAR
    ? null
    : r.InvalidDate;
}

// Phone is stored as "+CC digits" — isolate the digits part after the space.
export function getPhoneDigits(value: string): string {
  return value.includes(" ") ? value.split(" ").slice(1).join("") : value;
}

// Returns an error message if the value fails field-specific format rules,
// or null if the value is valid (or no rule applies for this ticketPersonMap).
function validateTicketPersonField(
  ticketPersonMap: string | null,
  value: string,
  r: Resources,
  producerId: number | null,
): string | null {
  switch (ticketPersonMap) {
    case "Email":
      return EMAIL_REGEX.test(value) ? null : r.InvalidEmail;
    case "Phone": {
      // No digits yet (e.g. only a CC was picked) — required-check owns "empty", not format-check.
      const digits = getPhoneDigits(value);
      return !digits || /^\d{6,15}$/.test(digits) ? null : r.InvalidPhone;
    }
    case "Age": {
      const age = Number(value);
      return Number.isInteger(age) && age >= 1 && age <= 100
        ? null
        : r.InvalidAgeError;
    }
    case "MemberCard":
      return isValidMemberCard(value) ? null : r.CardInvalid;
    case "FinancialSupportCoupon":
      return isValidFinancialSupportCoupon(value) ? null : r.CodeInvalid;
    case "SportsCard":
      if (producerId !== PRODUCER_ID_OLYMPIACOS) return null;
      return isValidSportsCard(value) ? null : r.CardInvalid;
    default:
      return null;
  }
}

const billingAutoFilledFields = {
  companyName: z.string().optional(),
  business: z.string().optional(),
  taxOffice: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  stateArea: z.string().optional(),
  country: z.string().optional(),
};

export function createReservationSchema(
  ticketPersonFormFields: TicketPersonFormField[],
  tickets: Ticket[],
  skipContactValidation = false,
  donationCampaign: DonationCampaign | null = null,
  r: Resources,
  isKokEvent = false,
  producerId: number | null = null,
  merchantTerms: MerchantTerm[] = [],
) {
  const contactSchema = skipContactValidation
    ? z.object({
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        email: z.string().optional(),
        mobileCountryCode: z.string().optional(),
        mobile: z.string().optional(),
      })
    : z.object({
        firstName: z.string().min(1, r.FieldRequired),
        lastName: z.string().min(1, r.FieldRequired),
        email: z
          .string()
          .min(1, r.FieldRequired)
          .refine(
            (v) => !v || EMAIL_REGEX.test(v),
            r.InvalidEmail,
          ),
        mobileCountryCode: z.string(),
        mobile: z
          .string()
          .min(1, r.FieldRequired)
          .refine(
            (v) => !v || (v.length >= 6 && v.length <= 15),
            r.InvalidPhone,
          )
          .refine(
            (v) => !v || /^\d+$/.test(v),
            r.InvalidPhoneFormat,
          ),
      });

  return z
    .object({
      contact: contactSchema,
      requiresShipping: z.boolean(),
      shipping: z.object({
        street: z.string().optional(),
        postcode: z.string().optional(),
        number: z.string().optional(),
        area: z.string().optional(),
        city: z.string().optional(),
        country: z.string().optional(),
      }),
      billing: z.discriminatedUnion("type", [
        z.object({
          type: z.literal("receipt"),
          vatNumber: z.string().optional(),
          ...billingAutoFilledFields,
        }),
        z.object({
          type: z.literal("invoice"),
          vatNumber: z.string().trim().min(1, r.FieldRequired), // The fields below are auto-filled via the VAT lookup and are read-only.
          isGreekCompany: z.boolean().optional(),
          ...billingAutoFilledFields,
        }),
      ]),
      shippingFee: z.number(),
      receiveUpdates: z.boolean(),
      saveInfoToAccount: z.boolean(),
      acceptTerms: z.boolean().refine((v) => v === true, {
        message: r.TermsRequired,
      }),
      deliveryMethodId: z.string(),
      donationAmount: z.number().default(0),
      ticketPersonData: z
        .record(z.string(), z.record(z.string(), z.string().nullish()))
        .optional(),
      merchantTerms: z.record(z.string(), z.boolean().optional()).optional(),
    })
    .superRefine((data, ctx) => {
      for (const term of merchantTerms) {
        if (term.isMandatory && !data.merchantTerms?.[String(term.termsId)]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["merchantTerms", String(term.termsId)],
            message: r.TermsRequired,
          });
        }
      }

      // Donation validation
      if (donationCampaign) {
        const { min, max } = getDonationAmountBounds(donationCampaign, tickets);
        const currency = donationCampaign.currencySymbol ?? "";
        const isDisabled = min > 0 && min === max;

        if (!isDisabled) {
          if (min > 0 && !data.donationAmount) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["donationAmount"],
              message: r.FieldRequired,
            });
          } else if (data.donationAmount < min || data.donationAmount > max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["donationAmount"],
              message: `${r.DonationAmountRange} ${formatPrice(min, currency)} ${r.And} ${formatPrice(max, currency)}. ${r.DonationAdjust}`,
            });
          }
        }
      }

      // Billing validation
      if (data.billing.type === "invoice") {
        if (data.billing.isGreekCompany === false) {
          const requiredForeignFields = [
            "companyName",
            "business",
            "taxOffice",
            "address",
            "city",
            "postalCode",
            "country",
          ] as const;
          for (const field of requiredForeignFields) {
            if (!data.billing[field]?.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["billing", field],
                message: r.FieldRequired,
              });
            }
          }
        } else {
          const vat = data.billing.vatNumber?.trim();
          if (vat && !/^\d{9}$/.test(vat)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["billing", "vatNumber"],
              message: r.InvalidVAT,
            });
          }
          if (!data.billing.companyName?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["billing", "companyName"],
              message: r.FieldRequired,
            });
          }
        }
      }

      // Shipping validation
      if (data.requiresShipping) {
        const requiredShippingFields: Array<
          [keyof typeof data.shipping, string]
        > = [
          ["street", r.FieldRequired],
          ["postcode", r.FieldRequired],
          ["number", r.FieldRequired],
          ["area", r.FieldRequired],
          ["city", r.FieldRequired],
          ["country", r.FieldRequired],
        ];

        for (const [field, message] of requiredShippingFields) {
          if (!data.shipping[field]) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["shipping", field],
              message,
            });
          }
        }
      }

      // Ticket person validation
      const kokAgeFieldId = getKokAgeFieldId(ticketPersonFormFields, isKokEvent);

      // Field lookups are stable across tickets, so resolve them once.
      const typeOfCertField = ticketPersonFormFields.find(
        (f) => f.ticketPersonMap === "TypeOfCertificate",
      );
      const certNumberField = ticketPersonFormFields.find(
        (f) => f.ticketPersonMap === "CertificationNumber",
      );
      const amkaField = ticketPersonFormFields.find(
        (f) => f.ticketPersonMap === AMKA_TICKET_PERSON_MAP,
      );

      // EVP-11038: collect AMKA values per-ticket and per-attribute so we can
      // emit in-batch duplicate errors after the per-ticket loop.
      const amkaEntries: Array<{ ticketId: string; attributeId: string; value: string }> = [];

      // EVP-11021: validation date is the platform "now" — captured once so all
      // tickets in the same submission share a deterministic boundary.
      const validationDate = new Date();

      const isOlympiacosEvent = producerId === PRODUCER_ID_OLYMPIACOS;

      for (const ticket of tickets) {
        const ageEligible = isKokEvent && kokAgeFieldId
          ? isKokAgeEligible(String(ticket.id), data.ticketPersonData ?? {}, kokAgeFieldId)
          : true;

        // Olympiacos: the fancard field is hidden on zero-price and kids-discount
        // tickets, so it must not be demanded here either — the server waives it too.
        const fancardWaived = isOlympiacosFancardWaived(ticket, isOlympiacosEvent);

        const ticketFields = ticket.formFields?.length
          ? ticketPersonFormFields.filter((f) =>
              ticket.formFields!.some((ff) => ff.id === f.dynamicAttributeId),
            )
          : [];

        for (const field of ticketFields) {
          if (field.dataTypeCode === DataTypeCode.warningMessage) continue;

          if (fancardWaived && field.ticketPersonMap === "SportsCard") continue;

          // Fields not required for free tickets are skipped entirely on a zero-price ticket
          if (field.notRequiredForFreeTickets && ticket.price === 0) continue;

          // KOK: skip required validation for conditional fields when age < 14
          if (
            isKokEvent &&
            field.ticketPersonMap &&
            KOK_AGE_CONDITIONAL_MAPS.has(field.ticketPersonMap) &&
            !ageEligible
          )
            continue;

          const value =
            data.ticketPersonData?.[String(ticket.id)]?.[
              field.dynamicAttributeId
            ];

          // Required-presence validation
          if (field.required) {
            const isInvalid =
              field.dataTypeCode === DataTypeCode.checkbox
                ? value !== "true"
                : field.ticketPersonMap === "Phone"
                  ? !value || !getPhoneDigits(value)
                  : !value;

            if (isInvalid) {
              let message = r.FieldRequired;
              if (field.ticketPersonMap === TYPE_OF_CERT_MAP) {
                const nationalityField = ticketPersonFormFields.find(
                  (f) => f.ticketPersonMap === NATIONALITY_MAP,
                );
                const nationalityValue = nationalityField
                  ? data.ticketPersonData?.[String(ticket.id)]?.[nationalityField.dynamicAttributeId]
                  : undefined;
                if (nationalityField && !nationalityValue) message = r.SelectNationalityFirst;
              }
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [
                  "ticketPersonData",
                  String(ticket.id),
                  field.dynamicAttributeId,
                ],
                message,
              });
              continue;
            }
          }

          // An optional date field left half-typed (e.g. only month+year) has no
          // real value to check below — see PARTIAL_DATE_SUFFIX.
          if (
            field.dataTypeCode === DataTypeCode.date &&
            !value &&
            data.ticketPersonData?.[String(ticket.id)]?.[
              `${field.dynamicAttributeId}${PARTIAL_DATE_SUFFIX}`
            ]
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [
                "ticketPersonData",
                String(ticket.id),
                field.dynamicAttributeId,
              ],
              message: r.InvalidDate,
            });
            continue;
          }

          // Format validation for standard mapped fields (e.g. Email, Phone, Age)
          // and generic date attributes (matched by dataTypeCode, not ticketPersonMap)
          if (value) {
            const formatError =
              field.dataTypeCode === DataTypeCode.date
                ? validateDateField(value, r)
                : validateTicketPersonField(
                    field.ticketPersonMap,
                    value,
                    r,
                    producerId,
                  );
            if (formatError) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [
                  "ticketPersonData",
                  String(ticket.id),
                  field.dynamicAttributeId,
                ],
                message: formatError,
              });
            }
          }
        }

        // AMKA validation: if TypeOfCertificate === AMKA, validate CertificationNumber
        if (typeOfCertField && certNumberField) {
          const certType =
            data.ticketPersonData?.[String(ticket.id)]?.[
              typeOfCertField.dynamicAttributeId
            ];
          const certNumber =
            data.ticketPersonData?.[String(ticket.id)]?.[
              certNumberField.dynamicAttributeId
            ];

          if (certType === AMKA_CERTIFICATE_VALUE && certNumber) {
            // Trim to match the server-side dedupe (person-validator.ashx).
            const trimmed = String(certNumber).trim();
            if (!isValidAmka(trimmed)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [
                  "ticketPersonData",
                  String(ticket.id),
                  certNumberField.dynamicAttributeId,
                ],
                message: r.InvalidCertification,
              });
            } else {
              // EVP-11038: track valid AMKAs for in-batch uniqueness check below.
              amkaEntries.push({
                ticketId: String(ticket.id),
                attributeId: certNumberField.dynamicAttributeId,
                value: trimmed,
              });

              // EVP-11021: kids-ticket discounts require minor AMKA. Standard
              // validity already passed (else branch); only check age here.
              if (
                ticket.discount?.isKidsTicket &&
                !isAmkaHolderMinor(String(certNumber), validationDate)
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [
                    "ticketPersonData",
                    String(ticket.id),
                    certNumberField.dynamicAttributeId,
                  ],
                  message: r.AmkaNotMinorForKidsTicket,
                });
              }
            }
          }
        }

        // EVP-11038: dedicated AMKA field (EVP-11021) is also covered by the uniqueness check.
        if (amkaField) {
          const amkaValue =
            data.ticketPersonData?.[String(ticket.id)]?.[amkaField.dynamicAttributeId];
          if (amkaValue) {
            const trimmed = String(amkaValue).trim();
            if (!isValidAmka(trimmed)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [
                  "ticketPersonData",
                  String(ticket.id),
                  amkaField.dynamicAttributeId,
                ],
                message: r.InvalidCertification,
              });
            } else {
              amkaEntries.push({
                ticketId: String(ticket.id),
                attributeId: amkaField.dynamicAttributeId,
                value: trimmed,
              });

              // EVP-11021: kids-ticket discounts require minor AMKA.
              if (
                ticket.discount?.isKidsTicket &&
                !isAmkaHolderMinor(String(amkaValue), validationDate)
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [
                    "ticketPersonData",
                    String(ticket.id),
                    amkaField.dynamicAttributeId,
                  ],
                  message: r.AmkaNotMinorForKidsTicket,
                });
              }
            }
          }
        }
      }

      // EVP-11038: block submission if the same AMKA appears on more than one ticket
      // within the current order. Server is the source of truth for cross-order checks.
      const seenAmkaCount = new Map<string, number>();
      for (const entry of amkaEntries) {
        seenAmkaCount.set(entry.value, (seenAmkaCount.get(entry.value) ?? 0) + 1);
      }
      for (const entry of amkaEntries) {
        if ((seenAmkaCount.get(entry.value) ?? 0) > 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["ticketPersonData", entry.ticketId, entry.attributeId],
            message: r.AmkaDuplicateInOrder,
          });
        }
      }
    });
}

export type ReservationFormValues = z.infer<
  ReturnType<typeof createReservationSchema>
>;
